import bgCover from '@/assets/image/pdfexport/bg-cover.png';
import podiumImg from '@/assets/image/pdfexport/podium.png';
import JsPdfExtendedBase, {
  FONTS,
} from '@/new-components/CRReportPDF/JsPdfExtended';

/** Column definition for consolidation table: group label and sub-keys for tableData/chartData. */
const CONSOLIDATION_COLUMN_DEFS = [
  {
    group: 'Total Cholesterol',
    dataKey: 'totalCholesterol',
    keys: [
      { key: 'high', label: 'High Cholesterol' },
      { key: 'normal', label: 'Normal Cholesterol' },
    ],
  },
  {
    group: 'LDL',
    dataKey: 'ldl',
    keys: [
      { key: 'high', label: 'High LDL' },
      { key: 'normal', label: 'Normal LDL' },
    ],
  },
  {
    group: 'HDL',
    dataKey: 'hdl',
    keys: [
      { key: 'low', label: 'Low HDL' },
      { key: 'normal', label: 'Normal HDL' },
    ],
  },
  {
    group: 'Triglyceride',
    dataKey: 'triglyceride',
    keys: [
      { key: 'high', label: 'High Triglyceride' },
      { key: 'normal', label: 'Normal Triglyceride' },
    ],
  },
  {
    group: 'Blood Glucose',
    dataKey: 'bloodGlucose',
    keys: [
      { key: 'high', label: 'High Blood Glucose' },
      { key: 'normal', label: 'Normal Blood Glucose' },
    ],
  },
  {
    group: 'Blood Pressure',
    dataKey: 'bloodPressure',
    keys: [
      { key: 'high', label: 'High BP' },
      { key: 'normal', label: 'Normal BP' },
    ],
  },
  {
    group: 'Body Mass Index',
    dataKey: 'bmi',
    keys: [
      { key: 'highRisk', label: 'High Risk' },
      { key: 'moderateRisk', label: 'Moderate Risk' },
      { key: 'lowRisk', label: 'Low-Risk' },
      { key: 'underweight', label: 'Underweight' },
    ],
  },
];

/**
 * Pure: build table model (header + rows) from item (chartData + tableData) for consolidation PDF.
 * @param {Object} item - { chartData, tableData }
 * @returns {{
 *   headerLabels: string[],
 *   groupSpans: number[],
 *   pctRows: Array<{ label: string, kind: 'gender' | 'subtotal' | 'grandTotal', values: string[] }>,
 *   numRows: Array<{ label: string, kind: 'gender' | 'subtotal' | 'grandTotal', values: (string|number)[] }>,
 *   grandTotal: number
 * }}
 */
function buildConsolidationTableModel(item) {
  const tableData = item?.tableData || {};
  const headerLabels = ['TestType'];
  CONSOLIDATION_COLUMN_DEFS.forEach((col) => {
    col.keys.forEach((k) => headerLabels.push(k.label));
  });

  const groupSpans = CONSOLIDATION_COLUMN_DEFS.map((col) => col.keys.length);

  // Lookup by label for chartData (female/male percentages per sub-label)
  const chartLabelMap = {};
  (item?.chartData || []).forEach((cat) => {
    (cat.items || []).forEach((sub) => {
      if (!sub?.label) return;
      chartLabelMap[sub.label] = {
        femalePct: Number(sub.femalePct) || 0,
        malePct: Number(sub.malePct) || 0,
      };
    });
  });

  const getValuesFrom = (source, formatter) => {
    const values = [];
    CONSOLIDATION_COLUMN_DEFS.forEach((col) => {
      const group = source && source[col.dataKey];
      col.keys.forEach((k) => {
        const v = group && group[k.key];
        values.push(formatter(v));
      });
    });
    return values;
  };

  const pct = tableData?.percentages || {};
  const num = tableData?.inNumber || {};
  const grandTotal =
    num?.totalCholesterol?.grandTotal ?? num?.ldl?.grandTotal ?? 0;

  // Helper: build gender % row from chartData
  const buildGenderPctRow = (genderKey) => {
    const values = [];
    CONSOLIDATION_COLUMN_DEFS.forEach((col) => {
      col.keys.forEach((k) => {
        const label = k.label;
        const m = chartLabelMap[label];
        if (!m) {
          values.push('-');
          return;
        }
        const pctValue = m[genderKey === 'female' ? 'femalePct' : 'malePct'];
        values.push(pctValue ? `${pctValue.toFixed(1)}%` : '0.0%');
      });
    });
    return values;
  };

  // Helper: build gender number row by splitting total using gender % share
  const buildGenderNumRow = (genderKey) => {
    const values = [];
    CONSOLIDATION_COLUMN_DEFS.forEach((col) => {
      const groupNum = num[col.dataKey] || {};
      col.keys.forEach((k) => {
        const totalCount = groupNum[k.key];
        if (totalCount == null) {
          values.push('-');
          return;
        }
        const lbl = k.label;
        const m = chartLabelMap[lbl];
        if (!m) {
          values.push('-');
          return;
        }
        const femalePct = m.femalePct;
        const malePct = m.malePct;
        const denom = femalePct + malePct;
        if (!denom) {
          values.push('-');
          return;
        }
        const share =
          genderKey === 'female' ? femalePct / denom : malePct / denom;
        const val = Math.round(totalCount * share);
        values.push(String(val));
      });
    });
    return values;
  };

  const pctRows = [];
  // Female / Male from chartData (if available)
  if (Object.keys(chartLabelMap).length) {
    pctRows.push({
      label: 'Female',
      kind: 'gender',
      values: buildGenderPctRow('female'),
    });
    pctRows.push({
      label: 'Male',
      kind: 'gender',
      values: buildGenderPctRow('male'),
    });
  }
  // Subtotal from tableData.percentages
  pctRows.push({
    label: 'Subtotal',
    kind: 'subtotal',
    values: getValuesFrom(pct, (v) =>
      v != null ? `${Number(v).toFixed(1)}%` : '-'
    ),
  });
  // GRAND TOTAL row – values not used for per-column drawing (we merge by group),
  // but keep 16 entries to keep shape consistent.
  pctRows.push({
    label: 'GRAND TOTAL',
    kind: 'grandTotal',
    values: getValuesFrom(pct, () => '100%'),
  });

  const numRows = [];
  if (Object.keys(chartLabelMap).length) {
    numRows.push({
      label: 'Female',
      kind: 'gender',
      values: buildGenderNumRow('female'),
    });
    numRows.push({
      label: 'Male',
      kind: 'gender',
      values: buildGenderNumRow('male'),
    });
  }
  numRows.push({
    label: 'Subtotal',
    kind: 'subtotal',
    values: getValuesFrom(num, (v) => (v != null ? String(v) : '-')),
  });
  numRows.push({
    label: 'GRAND TOTAL',
    kind: 'grandTotal',
    values: getValuesFrom(num, () => String(grandTotal)),
  });

  return { headerLabels, groupSpans, pctRows, numRows, grandTotal };
}

/** Default column widths: label col + 16 data cols (narrow for fit). */
const TABLE_LABEL_WIDTH = 72;
const TABLE_DATA_COL_WIDTH = 40;
const TABLE_ROW_HEIGHT = 14;
const TABLE_HEADER_ROW1_HEIGHT = 13;
const TABLE_HEADER_ROW2_HEIGHT = 28;
// CR Report PDF–style colors
const TABLE_HEADER_COLOR = '#E0E0E0';
const TABLE_HEADER_ROW1_COLOR = '#E0E0E0';
const TABLE_HEADER_ROW2_GROUP_COLORS = [
  '#FFF8E7', // Total Cholesterol – beige
  '#E3F2FD', // LDL – light blue
  '#FCE4EC', // HDL – light pink
  '#E8F5E9', // Triglyceride – light green
  '#E8EAF6', // Blood Glucose – lavender
  '#BBDEFB', // Blood Pressure – light blue
  '#FFE0B2', // Body Mass Index – peach
];
const TABLE_BODY_COLOR = '#F5F5F5';
const TABLE_SECTION_COLOR = '#E2D6F0';
const TABLE_CORNER_RADIUS = 6;

/** Map data column index (0..15) to group index (0..6) using groupSpans. */
function getGroupIndex(colIndex, groupSpans) {
  let sum = 0;
  for (let g = 0; g < groupSpans.length; g++) {
    if (colIndex < sum + groupSpans[g]) return g;
    sum += groupSpans[g];
  }
  return groupSpans.length - 1;
}

function metricKeyToLabel(metricKey) {
  const map = {
    bloodPressure: 'Blood Pressure',
    bmi: 'Body Mass Index',
    tc: 'Total Cholesterol',
    ldl: 'LDL',
    hdl: 'HDL',
    triglyceride: 'Triglyceride',
    randomGlucose: 'Blood Glucose',
  };
  return map[metricKey] || metricKey;
}

function formatPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const fixed = n.toFixed(1);
  return `${fixed.endsWith('.0') ? String(Math.round(n)) : fixed}%`;
}

function buildTableModelFromHealthScreenings(healthScreenings) {
  const groups = Object.entries(healthScreenings || {})
    .filter(([, v]) => Array.isArray(v?.categories) && v.categories.length > 0)
    .map(([metricKey, metric]) => ({
      group: metricKeyToLabel(metricKey),
      metric,
      categories: metric.categories || [],
    }));

  const groupSpans = groups.map((g) => g.categories.length);
  const pctRows = [
    {
      label: 'Female',
      kind: 'gender',
      values: groups.flatMap((g) =>
        g.categories.map((c) => formatPct(c?.femalePercent))
      ),
    },
    {
      label: 'Male',
      kind: 'gender',
      values: groups.flatMap((g) =>
        g.categories.map((c) => formatPct(c?.malePercent))
      ),
    },
    {
      label: 'Subtotal',
      kind: 'subtotal',
      values: groups.flatMap((g) =>
        g.categories.map((c) => formatPct(c?.percent))
      ),
    },
    {
      label: 'GRAND TOTAL',
      kind: 'grandTotal',
      values: [],
    },
  ];

  const numRows = [
    {
      label: 'Female',
      kind: 'gender',
      values: groups.flatMap((g) =>
        g.categories.map((c) => c?.femaleTotal ?? '')
      ),
    },
    {
      label: 'Male',
      kind: 'gender',
      values: groups.flatMap((g) =>
        g.categories.map((c) => c?.maleTotal ?? '')
      ),
    },
    {
      label: 'Subtotal',
      kind: 'subtotal',
      values: groups.flatMap((g) => g.categories.map((c) => c?.subTotal ?? '')),
    },
    {
      label: 'GRAND TOTAL',
      kind: 'grandTotal',
      values: [],
    },
  ];

  const grandTotalsByGroup = groups.map((g) => {
    // Prefer API 'total' for GRAND TOTAL row; fallback to sums.
    const metricTotal =
      g.metric?.total ?? g.metric?.totalOfAllTime ?? g.metric?.totalThisYear;
    if (metricTotal != null && metricTotal !== '') return metricTotal;
    const sumTotal = (g.categories || []).reduce(
      (sum, c) => sum + (Number(c?.total) || 0),
      0
    );
    if (sumTotal) return sumTotal;
    return (g.categories || []).reduce(
      (sum, c) => sum + (Number(c?.subTotal) || 0),
      0
    );
  });

  return { groups, groupSpans, pctRows, numRows, grandTotalsByGroup };
}

/**
 * Pure: build grid of rows/cells for consolidation table (CR Report PDF–style model).
 * Each row is array of { color, value, width, height }; merged cells use single cell with combined width.
 * @param {Object} item - { chartData, tableData }
 * @param {{ contentWidth?: number }} options - optional; if contentWidth set, data columns use (contentWidth - TABLE_LABEL_WIDTH) / 16
 * @returns {{ rows: Array<Array<{ color: string, value: string, width: number, height: number }>> }}
 */
function buildConsolidationTableGrid(item, options = {}) {
  const healthScreenings =
    item?.tableData?.healthScreenings || item?.healthScreenings || null;
  const isNewStructure = !!healthScreenings;

  const model = isNewStructure
    ? buildTableModelFromHealthScreenings(healthScreenings)
    : buildConsolidationTableModel(item);

  const {
    groupSpans,
    pctRows,
    numRows,
    grandTotal,
    groups,
    grandTotalsByGroup,
  } = isNewStructure
    ? { ...model, grandTotal: 0 }
    : { ...model, groups: CONSOLIDATION_COLUMN_DEFS, grandTotalsByGroup: null };

  const rows = [];
  const H = TABLE_ROW_HEIGHT;
  const totalDataCols = groupSpans.reduce((a, b) => a + b, 0) || 1;
  const dataColWidth =
    options.contentWidth != null
      ? (options.contentWidth - TABLE_LABEL_WIDTH) / totalDataCols
      : TABLE_DATA_COL_WIDTH;

  const H1 = TABLE_HEADER_ROW1_HEIGHT;
  const H2 = TABLE_HEADER_ROW2_HEIGHT;

  // Header row 1: TestType (rowSpan 2) + group cells
  const row0 = [
    {
      color: TABLE_HEADER_ROW1_COLOR,
      value: 'TestType',
      width: TABLE_LABEL_WIDTH,
      height: H1 + H2,
      rowSpan: 2,
    },
  ];
  (groups || []).forEach((col, idx) => {
    const w = groupSpans[idx] * dataColWidth;
    row0.push({
      color: TABLE_HEADER_ROW1_COLOR,
      value: col.group,
      width: w,
      height: H1,
    });
  });
  rows.push(row0);

  // Header row 2: sub-labels from API titles (new) or from defs (legacy)
  const row1 = [];
  if (isNewStructure) {
    (groups || []).forEach((col, groupIdx) => {
      const groupColor =
        TABLE_HEADER_ROW2_GROUP_COLORS[groupIdx] ?? TABLE_HEADER_COLOR;
      (col.categories || []).forEach((c) => {
        row1.push({
          color: groupColor,
          value: String(c?.title ?? ''),
          width: dataColWidth,
          height: H2,
        });
      });
    });
  } else {
    CONSOLIDATION_COLUMN_DEFS.forEach((col, groupIdx) => {
      const groupColor =
        TABLE_HEADER_ROW2_GROUP_COLORS[groupIdx] ?? TABLE_HEADER_COLOR;
      col.keys.forEach((k) => {
        row1.push({
          color: groupColor,
          value: k.label,
          width: dataColWidth,
          height: H2,
        });
      });
    });
  }
  rows.push(row1);

  // Percent block: Female, Male, Subtotal, GRAND TOTAL
  pctRows.forEach((row) => {
    if (row.kind === 'grandTotal') {
      const r = [
        {
          color: TABLE_BODY_COLOR,
          value: 'GRAND TOTAL',
          width: TABLE_LABEL_WIDTH,
          height: H,
        },
      ];
      (groups || []).forEach((col, idx) => {
        const w = groupSpans[idx] * dataColWidth;
        r.push({
          color: TABLE_HEADER_ROW2_GROUP_COLORS[idx] ?? TABLE_BODY_COLOR,
          value: '100%',
          width: w,
          height: H,
        });
      });
      rows.push(r);
    } else {
      const r = [
        {
          color: TABLE_BODY_COLOR,
          value: row.label,
          width: TABLE_LABEL_WIDTH,
          height: H,
        },
      ];
      row.values.forEach((v, cinx) => {
        const groupIdx = getGroupIndex(cinx, groupSpans);
        r.push({
          color: TABLE_HEADER_ROW2_GROUP_COLORS[groupIdx] ?? TABLE_BODY_COLOR,
          value: String(v),
          width: dataColWidth,
          height: H,
        });
      });
      rows.push(r);
    }
  });

  // Section row "In number" (colSpan full width)
  const tableDataWidth = totalDataCols * dataColWidth;
  const inNumberRow = [
    {
      color: TABLE_SECTION_COLOR,
      value: 'In number',
      width: TABLE_LABEL_WIDTH + tableDataWidth,
      height: H,
      colSpan: 17,
    },
  ];
  rows.push(inNumberRow);

  // In number block
  numRows.forEach((row) => {
    if (row.kind === 'grandTotal') {
      const r = [
        {
          color: TABLE_BODY_COLOR,
          value: 'GRAND TOTAL',
          width: TABLE_LABEL_WIDTH,
          height: H,
        },
      ];
      (groups || []).forEach((col, idx) => {
        const w = groupSpans[idx] * dataColWidth;
        r.push({
          color: TABLE_HEADER_ROW2_GROUP_COLORS[idx] ?? TABLE_BODY_COLOR,
          value:
            grandTotalsByGroup && grandTotalsByGroup[idx] != null
              ? String(grandTotalsByGroup[idx])
              : grandTotal
              ? String(grandTotal)
              : '',
          width: w,
          height: H,
        });
      });
      rows.push(r);
    } else {
      const r = [
        {
          color: TABLE_BODY_COLOR,
          value: row.label,
          width: TABLE_LABEL_WIDTH,
          height: H,
        },
      ];
      row.values.forEach((v, cinx) => {
        const groupIdx = getGroupIndex(cinx, groupSpans);
        r.push({
          color: TABLE_HEADER_ROW2_GROUP_COLORS[groupIdx] ?? TABLE_BODY_COLOR,
          value: String(v),
          width: dataColWidth,
          height: H,
        });
      });
      rows.push(r);
    }
  });

  return { rows };
}

/**
 * Consolidation-specific PDF: extends base and adds addConsolidationPage only.
 * Does not modify CRReportPDF.
 */
class JsPdfExtended extends JsPdfExtendedBase {
  /**
   * Draw table from grid model (CR Report PDF–style: rect per cell, no outer border).
   * @param {Array<Array<{ color: string, value: string, width: number, height: number }>>} rows
   * @param {number} x
   * @param {number} y
   * @returns {number} y after table
   */
  drawTableFromGrid(rows, x, y) {
    const pad = 5;
    const lineWidth = 0.5;
    const gridColor = [255, 255, 255]; // white separator lines
    const R = TABLE_CORNER_RADIUS;

    // Effective row height: for row0 with rowSpan use H1 only (one logical row), else row[0].height
    const getEffectiveRowHeight = (row, rinx) =>
      rinx === 0 && row[0]?.rowSpan
        ? row[1]?.height ?? TABLE_ROW_HEIGHT
        : row[0]?.height ?? TABLE_ROW_HEIGHT;

    // Table bounds for rounded clip
    const totalWidth =
      rows[0]?.reduce((sum, c) => sum + (c.width ?? 0), 0) ?? 0;
    const totalHeight = rows.reduce(
      (sum, row, rinx) => sum + getEffectiveRowHeight(row, rinx),
      0
    );

    this.saveGraphicsState();
    this.roundedRect(x, y, totalWidth, totalHeight, R, R, null);
    this.clip();

    const boundaries = []; // { yTop, yBottom, xEnds, skipFirstSegment? } per row
    let yPos = y;
    rows.forEach((row, rinx) => {
      const isHeaderRow = rinx < 2;
      const isRow1 = rinx === 1;
      const yTop = yPos;
      let xPos = x;
      const xEnds = [x];
      let rowHeight = getEffectiveRowHeight(row, rinx);
      let skipFirstSegment = false;

      if (isRow1) {
        // Header row 2: N cells (dynamic) – multi-line wrap + vertical center, smaller font to show full text
        xPos = x + TABLE_LABEL_WIDTH;
        xEnds.push(xPos);
        row.forEach((cell, cinx) => {
          const w = cell.width;
          const h = cell.height ?? TABLE_ROW_HEIGHT;
          this.setFillColor(cell.color).rect(xPos, yPos, w, h, 'F');
          this.setFontStyle({
            font: FONTS.OpenSans.semiBold,
            fontSize: 6,
          }).setTextColor(0, 0, 0);
          const text = String(cell.value ?? '');
          const maxW = w - pad * 2;
          const textLines = this.splitTextToSize(text, maxW);
          const lineHeight = this.getLineHeight();
          const maxLines = Math.max(1, Math.floor((h - pad * 2) / lineHeight));
          const linesToDraw = textLines.slice(0, maxLines);
          const blockHeight = linesToDraw.length * lineHeight;
          const textYStart = yPos + (h - blockHeight) / 2 + lineHeight - 2;
          let textY = textYStart;
          linesToDraw.forEach((line) => {
            this.text(line, xPos + w / 2 - this.getTextWidth(line) / 2, textY);
            textY += lineHeight;
          });
          xPos += w;
          xEnds.push(xPos);
        });
      } else {
        row.forEach((cell, cinx) => {
          const w = cell.width;
          const h = cell.height ?? TABLE_ROW_HEIGHT;
          const isFirstCell = rinx === 0 && cinx === 0;
          if (isFirstCell) {
            this.setFillColor(cell.color).roundedRect(
              xPos,
              yPos,
              w,
              h,
              R,
              R,
              'F'
            );
          } else {
            this.setFillColor(cell.color).rect(xPos, yPos, w, h, 'F');
          }
          this.setFontStyle({
            font:
              isHeaderRow ||
              cell.value === 'GRAND TOTAL' ||
              cell.value === 'In number'
                ? FONTS.OpenSans.semiBold
                : FONTS.OpenSans.regular,
            fontSize: isHeaderRow
              ? 8
              : cinx === 0 && cell.value === 'In number'
              ? 9
              : 7,
          }).setTextColor(0, 0, 0);
          const text = String(cell.value ?? '');
          const maxW = w - pad * 2;
          const lines = this.splitTextToSize(text, maxW);
          const line1 = lines[0] || '';
          if (isFirstCell) {
            const lineHeight = this.getLineHeight();
            const textX = xPos + w / 2 - this.getTextWidth(line1) / 2;
            const textY = yPos + (h - lineHeight) / 2 + lineHeight - 2;
            this.text(line1, textX, textY);
          } else if (rinx === 0 && isHeaderRow) {
            const lineHeight = this.getLineHeight();
            const maxLines = Math.max(
              1,
              Math.floor((h - pad * 2) / lineHeight)
            );
            const linesToDraw = lines.slice(0, maxLines);
            const blockHeight = linesToDraw.length * lineHeight;
            const textYStart = yPos + (h - blockHeight) / 2 + lineHeight - 2;
            let textY = textYStart;
            linesToDraw.forEach((line) => {
              this.text(
                line,
                xPos + w / 2 - this.getTextWidth(line) / 2,
                textY
              );
              textY += lineHeight;
            });
          } else if (cinx === 0) {
            this.text(line1, xPos + pad, yPos + 10);
          } else {
            this.text(
              line1,
              xPos + w / 2 - this.getTextWidth(line1) / 2,
              yPos + 10
            );
          }
          xPos += w;
          xEnds.push(xPos);
        });
        if (rinx === 0 && row[0]?.rowSpan) skipFirstSegment = true;
      }

      boundaries.push({
        yTop,
        yBottom: yPos + rowHeight,
        xEnds,
        skipFirstSegment,
      });
      yPos += rowHeight;
    });

    // Draw thin white grid lines between cells (like CR Report PDF reference)
    this.setDrawColor(...gridColor);
    this.setLineWidth(lineWidth);
    boundaries.forEach(({ yTop, yBottom, xEnds, skipFirstSegment }) => {
      for (let i = 1; i < xEnds.length - 1; i++) {
        this.line(xEnds[i], yTop, xEnds[i], yBottom);
      }
      const xStart = skipFirstSegment ? xEnds[1] : xEnds[0];
      this.line(xStart, yBottom, xEnds[xEnds.length - 1], yBottom);
    });

    this.restoreGraphicsState();
    return yPos;
  }

  /**
   * Draw consolidation data table (percentages + in number). Uses grid model and CR Report PDF–style drawing.
   * @param {Object} item - consolidation item (contains chartData + tableData)
   * @param {{ x: number, y: number }} origin
   * @returns {number} y position after table (for chaining)
   */
  renderConsolidationTable(item, origin, options = {}) {
    if (!item || !item.tableData) return origin.y;
    const { rows } = buildConsolidationTableGrid(item, options);
    return this.drawTableFromGrid(rows, origin.x, origin.y);
  }

  /**
   * One consolidation page: horizontal bar chart + table (Percentages + In number).
   * item: { businessUnit, year, chartData, tableData }
   * chartInstance: optional Chart.js instance; when provided, its canvas is exported as image instead of drawing bars.
   */
  addConsolidationPage(item, chartInstance) {
    if (!item || !item.chartData) return this;

    this.addPage('a4', 'l');
    this.addImage(bgCover, 'PNG', 0, 0, 842, 595);
    const key = `Consolidation_${(item.businessUnit || '').replace(
      /\s+/g,
      ''
    )}`;
    this.addPaging(
      key,
      `Health Screening Breakdown – ${item.businessUnit || 'N/A'}`
    );

    const xLeft = 30;
    const yStart = 50;
    const yChartStart = yStart + 70;
    const chartImgHeight = 280;

    const pageWidth =
      this.internal.pageSize.getWidth != null
        ? this.internal.pageSize.getWidth()
        : this.internal.pageSize.width;
    const contentWidth = pageWidth - xLeft - this.margins.right;

    this.addSubTitle(item.businessUnit || 'N/A', xLeft, yStart);

    this.addHeaderSection(
      xLeft,
      yStart + 24,
      `${item.year || ''}  ${item.groupValue || ''}`,
      podiumImg,
      contentWidth - 38
    );

    let y;

    if (chartInstance && chartInstance.ctx && chartInstance.ctx.canvas) {
      const legendBlockWidth = 140;
      const gap = 15;
      const xLegend = pageWidth - this.margins.right - legendBlockWidth;
      const chartImgWidth = xLegend - gap - xLeft;

      const chartImg = chartInstance.ctx.canvas.toDataURL('image/png');
      this.addImage(
        chartImg,
        'PNG',
        xLeft,
        yChartStart,
        chartImgWidth,
        chartImgHeight
      );
      const LABELS = [
        { color: '#F5C5D5', title: 'Female' },
        { color: '#E2F2FF', title: 'Male' },
      ];
      const legendHeight = LABELS.length * 30;
      const yLegend = yChartStart + (chartImgHeight - legendHeight) / 2;
      this.addLegendLabelChart(LABELS, xLegend, yLegend);
      y = yChartStart + chartImgHeight + 20;
    } else {
      const xChartStart = 205;
      const chartWidth = 320;
      const values = [];
      (item.chartData || []).forEach((cat) => {
        (cat.items || []).forEach((sub) => {
          values.push(Number(sub.femalePct) || 0, Number(sub.malePct) || 0);
        });
      });
      const rawMax = values.length ? Math.max(...values) : 0;
      const maxPct = Math.min(100, Math.max(10, Math.ceil(rawMax / 10) * 10));
      const scale = chartWidth / maxPct;
      const rowHeight = 13;
      const femaleColor = '#F5C5D5';
      const maleColor = '#E2F2FF';
      y = yChartStart;

      (item.chartData || []).forEach((cat) => {
        (cat.items || []).forEach((sub) => {
          const femalePct = Math.min(Number(sub.femalePct) || 0, maxPct);
          const malePct = Math.min(Number(sub.malePct) || 0, maxPct);
          const label = sub.label || '';

          this.setFontStyle({
            font: FONTS.OpenSans.regular,
            fontSize: 8,
          }).setTextColor(0, 0, 0);
          this.text(label.substring(0, 28), xLeft, y + 10);

          const barY = y + 2;
          const barH = 8;
          if (femalePct > 0) {
            this.setFillColor(femaleColor).rect(
              xChartStart,
              barY,
              femalePct * scale,
              barH,
              'F'
            );
            this.setFontStyle({ fontSize: 7 })
              .setTextColor(0, 0, 0)
              .text(
                `${femalePct}%`,
                xChartStart + femalePct * scale + 4,
                y + 9
              );
          }
          if (malePct > 0) {
            this.setFillColor(maleColor).rect(
              xChartStart,
              barY + barH + 1,
              malePct * scale,
              barH,
              'F'
            );
            this.setFontStyle({ fontSize: 7 })
              .setTextColor(0, 0, 0)
              .text(
                `${malePct}%`,
                xChartStart + malePct * scale + 4,
                y + 9 + barH + 1
              );
          }

          y += rowHeight * 2 + 4;
        });
      });

      y += 16;
    }

    if (item.tableData) {
      y = this.renderConsolidationTable(
        item,
        { x: xLeft, y: y - 5 },
        { contentWidth }
      );
    }

    return this;
  }
}

export default JsPdfExtended;
